import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  type MutationFunction,
  useRequest,
  type QuoteFileCategory,
  allQuoteFilesListKey,
  QuoteFilesListResponseBody,
} from '@frontend/api';

type BulkUpdateQuoteFilesRequestBody = {
  uuids: string[];
  category?: QuoteFileCategory;
  promotedInId?: number | null;
};

type UseBulkUpdateQuoteFilesParams = {
  quoteId: number;
};

export const useBulkUpdateQuoteFiles: MutationFunction<
  [],
  BulkUpdateQuoteFilesRequestBody,
  [UseBulkUpdateQuoteFilesParams]
> = (params, { requestOptions, queryOptions } = {}) => {
  const queryClient = useQueryClient();
  const urlPath = `/quotes/${params.quoteId}/files/bulk_update`;
  const request = useRequest(requestOptions);

  const { onMutate, onError, onSettled, ...restQueryOptions } =
    queryOptions ?? {};

  return useMutation({
    mutationFn: (requestData) =>
      request(urlPath, {
        init: { method: 'PATCH', body: requestData },
      }),
    ...restQueryOptions,
    mutationKey: ['quotes', 'quoteFiles', 'bulkUpdate', params],
    onMutate: async (requestData) => {
      onMutate?.(requestData);

      await queryClient.cancelQueries({ queryKey: allQuoteFilesListKey });
      const previousQueryData = queryClient.getQueryData(allQuoteFilesListKey);

      queryClient.setQueriesData<QuoteFilesListResponseBody | undefined>(
        { queryKey: allQuoteFilesListKey },
        (oldData) => {
          if (!oldData) return undefined;

          return {
            ...oldData,
            results: oldData.results.map((file) => {
              if (requestData.uuids.includes(file.uuid)) {
                return {
                  ...file,
                  ...requestData,
                };
              }

              return file;
            }),
          };
        },
      );

      return { previousQueryData };
    },
    onError: (...args) => {
      onError?.(...args);

      const [, , context] = args;
      queryClient.setQueryData(
        allQuoteFilesListKey,
        context?.previousQueryData,
      );
    },
    onSettled: (...args) => {
      onSettled?.(...args);

      queryClient.invalidateQueries({ queryKey: allQuoteFilesListKey });
    },
  });
};
